/**
 * CJAS — per-day prayer time overrides.  Jira CA-22.
 *
 * The annual timetable is the base. Overrides are what actually happens: Eid,
 * the Ramadan Isha shift, a funeral that moves Asr, a burst pipe that closes
 * the mosque on a Tuesday.
 *
 * The rule that governs this file: an override NEVER changes a "begins" time.
 * Those are facts about the sun and the mosque does not get a vote. An
 * override changes jamaat times, closures and notes — nothing else.
 */

const prayer = require('./prayer-times');

const OVERRIDABLE = ['fajr_jamaat','dhuhr_jamaat','asr_jamaat','maghrib_jamaat','isha_jamaat',
                     'jumua_khutbah','jumua_jamaat','is_closed','note_no'];

const PROTECTED = ['fajr_begins','dhuhr_begins','asr_begins','maghrib_begins','isha_begins','sunrise'];

const REASONS = ['eid','ramadan','closure','funeral','maintenance','weather','other'];

/**
 * What a member reads for each reason.  ADDED 2026-09-10.
 *
 * An override's reason is stored as a CODE (`reason_no` holds 'maintenance',
 * because validate() accepts nothing else), and the API served the code as
 * it was — so the app printed "maintenance" to a Norwegian reader and to an
 * Urdu one alike. The label is fixed vocabulary, not staff prose, so it lives
 * here in three languages rather than going through machine translation;
 * the Norwegian matches the panel's own list and the Urdu the WordPress
 * plugin's (class-cjas-lang.php), which surfaces should now read from the
 * API's `overrideReasonLabel` instead of keeping copies. Urdu to be confirmed
 * by CJAS alongside the rest of the UI strings (CA-91).
 */
const REASON_LABELS = {
  eid:         { no: 'Eid',                  en: 'Eid',               ur: 'عید' },
  ramadan:     { no: 'Ramadan',              en: 'Ramadan',           ur: 'رمضان' },
  closure:     { no: 'Stengt',               en: 'Closed',            ur: 'بندش' },
  funeral:     { no: 'Janazah / begravelse', en: 'Janazah / funeral', ur: 'نمازِ جنازہ' },
  maintenance: { no: 'Vedlikehold',          en: 'Maintenance',       ur: 'مرمت' },
  weather:     { no: 'Vær',                  en: 'Weather',           ur: 'موسم' },
  other:       { no: 'Annet',                en: 'Other',             ur: 'دیگر' },
};
const LABEL_CHAIN = { ur: ['ur', 'en', 'no'], en: ['en', 'no'], no: ['no'] };

/**
 * The label for a reason code in the reader's language, with the language it
 * actually came back in (UR → EN → NO, like every translated field). A value
 * that is not a known code — a row written before validation — is returned
 * as it is, as Norwegian.
 */
function reasonLabel(code, lang = 'no') {
  if (code == null || code === '') return null;
  const labels = REASON_LABELS[code];
  if (!labels) return { code: String(code), label: String(code), lang: 'no' };
  for (const l of LABEL_CHAIN[lang] || LABEL_CHAIN.no) {
    if (labels[l]) return { code, label: labels[l], lang: l };
  }
  return { code, label: labels.no, lang: 'no' };
}

const iso = d => {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function isTime(v) { return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(v)); }

/** Every date in a range, inclusive. Local dates, never UTC. */
function datesBetween(fromStr, toStr) {
  const [fy, fm, fd] = fromStr.split('-').map(Number);
  const [ty, tm, td] = toStr.split('-').map(Number);
  const from = new Date(fy, fm - 1, fd), to = new Date(ty, tm - 1, td);
  if (to < from) return [];
  const out = [];
  for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) out.push(iso(d));
  return out;
}

/** The calendar day after a "YYYY-MM-DD" (local calendar arithmetic, as datesBetween). */
function nextIso(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return iso(new Date(y, m - 1, d + 1));
}

const PRAYER_LABEL = { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' };

/**
 * A jamaat cannot be held before its prayer has begun.  ADDED 2026-09-10.
 *
 * `validate()` checked the SHAPE of a time and nothing about what it meant, so
 * `{"isha_jamaat":"22:05"}` on a day Isha begins 22:20 went to every screen in
 * the building: a congregation called to a prayer that is not yet due.
 *
 * Wall-clock arithmetic only (prayer-times.js: a timetable time is never an
 * instant), and the one trap is the one prayer-times.js exists for — Isha
 * past midnight. Minutes are counted from the row's own midnight:
 *  - each BEGINS time gets its day from `dayOffsets()` over the begins row, so
 *    an Oslo-June `isha_begins 00:35` is 24:35, not 00:35;
 *  - an ISHA jamaat earlier on the clock than that row's Fajr begins can only
 *    be the small hours after midnight (nobody holds Isha before Fajr of the
 *    same date), so it is counted as the next day: begins 23:40 / jamaat 00:10
 *    is 20 minutes after, not 23½ hours before. Any other Isha jamaat is that
 *    evening — so 23:50 against a 00:35 begins is refused, correctly;
 *  - the other four never cross midnight at this latitude, and a jamaat on
 *    the clock before its begins is simply before it.
 * `baseDay` is the timetable row (`*_begins` keys, or a resolved day's
 * `begins` object). Without one there is nothing to compare, and nothing is
 * refused on this ground.
 */
function beginsOf(baseDay) {
  if (!baseDay) return null;
  if (baseDay.begins) return { ...baseDay.begins };
  const out = {};
  for (const p of prayer.PRAYERS) out[p] = baseDay[`${p}_begins`] || null;
  return out;
}

function jamaatAfterBegins(patch, baseDay) {
  const begins = beginsOf(baseDay);
  if (!begins) return [];
  const safe = (v) => (isTime(v) ? prayer.toMinutes(v) : null);
  const order = prayer.PRAYERS;
  const offsets = prayer.dayOffsets(order.map((p) => (isTime(begins[p]) ? begins[p] : null)));
  const errors = [];
  order.forEach((p, i) => {
    const key = `${p}_jamaat`;
    const j = safe(patch && patch[key]);
    const b = safe(begins[p]);
    if (j == null || b == null) return;
    const beginsAt = b + offsets[i] * 1440;
    let jamaatAt = j + offsets[i] * 1440;
    if (p === 'isha') {
      const fajr = safe(begins.fajr);
      jamaatAt = fajr != null && j < fajr ? j + 1440 : j;
    }
    if (jamaatAt < beginsAt) {
      errors.push({ field: key, code: 'before_begins',
        message: `${PRAYER_LABEL[p]}-jamaat ${patch[key]} er før ${PRAYER_LABEL[p]} begynner (${begins[p]}). Jamaat kan ikke holdes før bønnetiden har begynt.` });
    }
  });
  return errors;
}

/**
 * …nor after its prayer's time has run out.  Audit F-01, 2026-10-19.
 *
 * jamaatAfterBegins() held a jamaat to the START of its window only. cjas's IRN period said Fajr 07:30 through
 * 31 October, and from 25 October (the clocks went back, sunrise moved to 07:21) every surface called members to a Fajr
 * jamaat after sunrise — when Fajr is over. The window ends are the ones prayer-times.windowEnd() publishes, from the
 * same sheet: Fajr at sunrise, Dhuhr (and Jumu'ah) when Asr begins, Asr when Maghrib begins, Maghrib when Isha begins,
 * Isha at the next morning's Fajr. A jamaat AT the end is refused too: the time is over at that minute.
 *
 * Isha's end is on ANOTHER day's sheet. With `nextDay` (a prayer_day row or a resolved day) it is that day's Fajr; without
 * one the row's own Fajr stands in — a day apart they differ by a few minutes, and an Isha jamaat within minutes of Fajr
 * is wrong either way. The minute arithmetic is jamaatAfterBegins()'s (each begins time on its own day by dayOffsets(),
 * an Isha jamaat before the row's Fajr counted as after midnight), so the two checks agree on what a time means.
 */
const WINDOW_END = { fajr: 'sunrise', dhuhr: 'asr', asr: 'maghrib', maghrib: 'isha', isha: 'fajr' };
const END_LABEL = { fajr: 'soloppgang', dhuhr: 'Asr begynner', asr: 'Maghrib begynner', maghrib: 'Isha begynner', isha: 'Fajr begynner' };

function jamaatInWindow(patch, baseDay, nextDay = null) {
  const begins = beginsOf(baseDay);
  if (!begins) return [];
  begins.sunrise = (baseDay.begins ? baseDay.begins.sunrise : baseDay.sunrise) || null;
  const safe = (v) => (isTime(v) ? prayer.toMinutes(v) : null);
  const keys = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
  const offsets = prayer.dayOffsets(keys.map((k) => (isTime(begins[k]) ? begins[k] : null)));
  const abs = (k) => { const m = safe(begins[k]); return m == null ? null : m + offsets[keys.indexOf(k)] * 1440; };
  const nextFajr = nextDay ? safe(beginsOf(nextDay).fajr) : null;
  const errors = [];
  for (const p of prayer.PRAYERS) {
    const key = `${p}_jamaat`;
    const j = safe(patch && patch[key]);
    if (j == null || safe(begins[p]) == null) continue;
    let jamaatAt = j + offsets[keys.indexOf(p)] * 1440, endAt, ends;
    if (p === 'isha') {
      const fajr = safe(begins.fajr);
      jamaatAt = fajr != null && j < fajr ? j + 1440 : j;
      const f = nextFajr != null ? nextFajr : fajr;
      if (f == null) continue;
      endAt = f + 1440;
      ends = prayer.toHHMM(f);
    } else {
      endAt = abs(WINDOW_END[p]);
      ends = begins[WINDOW_END[p]];
      if (endAt == null) continue;
    }
    if (jamaatAt >= endAt) {
      errors.push({ field: key, code: 'after_window', ends,
        message: `${PRAYER_LABEL[p]}-jamaat ${patch[key]} er ikke før ${END_LABEL[p]} (${ends}). Jamaat kan ikke holdes etter at bønnetiden er ute.` });
    }
  }
  return errors;
}

/**
 * Validate one override before it is written.
 * Rejecting a protected field is not pedantry: an admin who "fixes" a wrong
 * sunrise here has silently broken sehri (CA-56), which is a fast broken.
 * With the day's timetable row it also refuses a jamaat before its prayer
 * begins (see jamaatAfterBegins).
 */
function validate(patch, reason, baseDay = null) {
  const errors = [];
  if (!REASONS.includes(reason)) errors.push({ field: 'reason', message: `Ukjent årsak: ${reason}.` });

  for (const key of Object.keys(patch || {})) {
    if (PROTECTED.includes(key)) {
      errors.push({ field: key, message: `"${key}" kan ikke overstyres — dette er en soltid, ikke en jamaat-tid.` });
      continue;
    }
    if (!OVERRIDABLE.includes(key)) {
      errors.push({ field: key, message: `"${key}" er ikke et felt som kan overstyres.` });
      continue;
    }
    const v = patch[key];
    if (key === 'is_closed') { if (typeof v !== 'boolean') errors.push({ field: key, message: 'Må være av/på.' }); continue; }
    if (key === 'note_no') { if (String(v).length > 200) errors.push({ field: key, message: 'Notatet er for langt (maks 200 tegn).' }); continue; }
    if (v !== null && !isTime(v)) errors.push({ field: key, message: `Ugyldig klokkeslett "${v}". Bruk TT:MM.` });
  }
  errors.push(...jamaatAfterBegins(patch, baseDay), ...jamaatInWindow(patch, baseDay));
  return { ok: errors.length === 0, errors };
}

/**
 * Apply overrides on top of a base day. This mirrors the SQL view
 * prayer_day_resolved — if the two ever disagree, the app and the TV screens
 * show different times, which is exactly the failure this platform exists to
 * stop.
 */
function resolve(baseDay, override) {
  if (!override) return { ...baseDay, overridden: false, overriddenFields: [] };
  const out = { ...baseDay };
  const applied = [];
  for (const key of OVERRIDABLE) {
    if (override[key] === undefined || override[key] === null) continue;
    out[key] = override[key];
    // A note is an annotation, not a changed time. Counting it here would put
    // an "ENDRET" badge on a day whose times nobody touched.
    if (key !== 'note_no') applied.push(key);
  }
  return { ...out, overridden: applied.length > 0, overriddenFields: applied,
           overrideReason: override.reason || null, overrideNote: override.note_no || null };
}

/**
 * A closed day still shows its begins times.
 * Members plan around them and pray at home; blanking the whole day tells
 * them nothing and looks like the app is broken.
 */
function closedDayView(resolvedDay) {
  if (!resolvedDay.is_closed) return { closed: false };
  return {
    closed: true,
    showBegins: true,
    hideJamaat: true,
    note: resolvedDay.overrideNote || null,
    reason: resolvedDay.overrideReason || null,
  };
}

/**
 * Bulk apply — the whole point of the feature. Ramadan is thirty days of the
 * same Isha shift, and nobody is editing thirty rows by hand at 23:00.
 */
function bulk(fromStr, toStr, patch, reason, existing = [], baseDays = []) {
  const check = validate(patch, reason);
  if (!check.ok) return { ok: false, ...check, overrides: [] };

  const dates = datesBetween(fromStr, toStr);
  if (!dates.length) return { ok: false, errors: [{ field: 'range', message: 'Til-dato er før fra-dato.' }], overrides: [] };

  // Thirty days of the same Isha shift is thirty different Isha begins times.
  // One day where the shifted jamaat falls before begins refuses the lot and
  // names the day — a range saved with a hole in it is worse than none.
  const baseByDate = new Map((baseDays || []).map(d => [d.date, d]));
  const early = [];
  for (const date of dates) {
    // …and after its time runs out (F-01), with the next day's row for Isha's end where the range has it.
    const base = baseByDate.get(date), after = baseByDate.get(nextIso(date));
    for (const e of [...jamaatAfterBegins(patch, base), ...jamaatInWindow(patch, base, after || null)]) early.push({ ...e, date, message: `${date}: ${e.message}` });
  }
  if (early.length) return { ok: false, errors: early, overrides: [] };

  const existingByDate = new Map(existing.map(o => [o.date, o]));
  const overrides = [], conflicts = [];

  for (const date of dates) {
    const prev = existingByDate.get(date);
    if (prev) {
      const clashing = Object.keys(patch).filter(k => prev[k] !== undefined && prev[k] !== null && prev[k] !== patch[k]);
      if (clashing.length) conflicts.push({ date, fields: clashing, existingReason: prev.reason });
    }
    overrides.push({ date, reason, ...patch });
  }

  return { ok: true, errors: [], overrides, conflicts, days: dates.length,
    summary: `${dates.length} dager · ${reason}${conflicts.length ? ` · ${conflicts.length} konflikt(er)` : ''}` };
}

/**
 * Conflicts are surfaced, never silently resolved. An Eid override landing on
 * top of a Ramadan block is almost always correct and almost always worth a
 * human glance — the one case where it is a mistake is expensive.
 */
function needsConfirmation(bulkResult) {
  return Boolean(bulkResult.ok && bulkResult.conflicts && bulkResult.conflicts.length);
}

/** Removing an override returns the day to the timetable — nothing is lost. */
function revert(baseDay) {
  return { ...baseDay, overridden: false, overriddenFields: [], overrideReason: null, overrideNote: null };
}

/**
 * Upcoming overrides for the admin dashboard, soonest first. Past ones are
 * dropped: a list that grows forever stops being read.
 */
function upcoming(overrides, now = new Date(), days = 60) {
  const today = iso(now);
  const limit = iso(new Date(now.getTime() + days * 86400000));
  return (overrides || [])
    .filter(o => o.date >= today && o.date <= limit)
    .sort((a, b) => a.date.localeCompare(b.date));
}

module.exports = {
  OVERRIDABLE, PROTECTED, REASONS, REASON_LABELS, reasonLabel,
  datesBetween, validate, jamaatAfterBegins, jamaatInWindow, resolve, closedDayView, bulk, needsConfirmation, revert, upcoming,
};
